/** Build-Zeit-Label der aktiven LEXCORE-Normenbasis (`VITE_LEXCORE_NORMENBASIS`). */

const FULL_SNAPSHOT_RE = /^ris-mietrecht-(\d{4})(\d{2})(\d{2})-v\d+$/i;
const SHORT_SNAPSHOT_RE = /^\d{4}$/;

export interface LexcoreSnapshotInfo {
  /** Rohwert aus Build-Env (z. B. `ris-mietrecht-20260607-v1` oder `0607`). */
  raw: string;
  /** Kurzlabel für Badge (z. B. `0607`). */
  shortLabel: string;
  /** Anzeige im Portal (z. B. `Rechtsstand · 0607`). */
  badgeLabel: string;
  /** Tooltip für Anwält:innen. */
  title: string;
  /** Lesbares Datum aus Snapshot-ID (z. B. `07.06.2026`), sonst null. */
  snapshotDate: string | null;
  /** Kurzbeschreibung des Pakets (z. B. MRG-Mietrecht). */
  packageLabel: string;
}

/** Alias für Badge-/Footer-Anzeige (Kurzlabel mit Präfix). */
export function getLexcoreSnapshotLabel(): string | null {
  return getLexcoreSnapshotInfo()?.badgeLabel ?? null;
}

export function resolveLexcoreSnapshotDate(raw: string): string | null {
  const trimmed = raw.trim();
  const full = trimmed.match(FULL_SNAPSHOT_RE);
  if (full) return `${full[3]}.${full[2]}.${full[1]}`;
  const embedded = trimmed.match(/(\d{4})(\d{2})(\d{2})/);
  if (embedded) return `${embedded[3]}.${embedded[2]}.${embedded[1]}`;
  return null;
}

function resolveLexcorePackageLabel(raw: string): string {
  if (/mietrecht/i.test(raw)) return 'MRG / Mietrecht (Phase 1)';
  return 'LEXCORE-Normenbasis';
}

export function resolveLexcoreShortLabel(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return '';

  const full = trimmed.match(FULL_SNAPSHOT_RE);
  if (full) return `${full[2]}${full[3]}`;

  if (SHORT_SNAPSHOT_RE.test(trimmed)) return trimmed;

  const embedded = trimmed.match(/(\d{4})(\d{2})(\d{2})/);
  if (embedded) return `${embedded[2]}${embedded[3]}`;

  return trimmed.length > 8 ? trimmed.slice(-4) : trimmed;
}

export function getLexcoreSnapshotInfo(): LexcoreSnapshotInfo | null {
  const raw = (import.meta.env.VITE_LEXCORE_NORMENBASIS ?? '').trim();
  if (!raw) return null;

  const shortLabel = resolveLexcoreShortLabel(raw);
  if (!shortLabel) return null;

  return {
    raw,
    shortLabel,
    badgeLabel: `Rechtsstand · ${shortLabel}`,
    title:
      `Aktuelle Normenbasis (Stand ${shortLabel}). ` +
      'Rechtsfragen in ARKO beziehen sich auf diesen kuratierten Gesetzesstand.',
    snapshotDate: resolveLexcoreSnapshotDate(raw),
    packageLabel: resolveLexcorePackageLabel(raw),
  };
}
